(() => {
  const componentName = 'vertical-announcement';
  const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

  if (customElements.get(componentName)) return;

  class VerticalAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
      if (this.slides.length === 0) return;

      this.currentIndex = Math.max(
        this.slides.findIndex((slide) => slide.classList.contains('is-active')),
        0
      );
      this.interval = Math.max(Number(this.dataset.interval) || 4000, 2000);
      this.transitionDuration = Math.max(Number(this.dataset.transitionDuration) || 500, 0);
      this.timer = null;
      this.focusResumeTimer = null;
      this.isTransitioning = false;
      this.activeAnimations = [];
      this.pauseReasons = new Set();

      this.handlePointerEnter = () => this.pause('hover');
      this.handlePointerLeave = () => this.resume('hover');
      this.handleFocusIn = (event) => {
        if (event.target.matches?.(':focus-visible')) this.pause('focus');
      };
      this.handleFocusOut = () => {
        window.clearTimeout(this.focusResumeTimer);
        this.focusResumeTimer = window.setTimeout(() => {
          if (!this.contains(document.activeElement)) this.resume('focus');
        }, 0);
      };
      this.handleKeyDown = (event) => {
        if (event.target !== this || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.show(this.currentIndex + direction, { direction });
      };
      this.handleVisibilityChange = () => {
        if (document.hidden) {
          this.stop();
        } else {
          this.start();
        }
      };
      this.handleBlockSelect = (event) => {
        const selectedBlockId = event.detail?.blockId;
        const selectedSlide = this.slides.find((slide) => slide.dataset.blockId === selectedBlockId);
        if (!selectedSlide) return;

        const selectedIndex = this.slides.indexOf(selectedSlide);
        if (selectedIndex < 0) return;

        this.show(selectedIndex, { immediate: true });
        this.start();
      };
      this.handleBlockDeselect = () => this.start();

      this.slides.forEach((slide, index) => {
        this.resetSlide(slide, index === this.currentIndex);
      });

      if (this.hasAttribute('data-pause-on-hover')) {
        this.addEventListener('mouseenter', this.handlePointerEnter);
        this.addEventListener('mouseleave', this.handlePointerLeave);
      }
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('keydown', this.handleKeyDown);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.cancelAnimations();
      window.clearTimeout(this.focusResumeTimer);

      if (this.hasAttribute('data-pause-on-hover')) {
        this.removeEventListener('mouseenter', this.handlePointerEnter);
        this.removeEventListener('mouseleave', this.handlePointerLeave);
      }
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('keydown', this.handleKeyDown);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    }

    setSlideAccessibility(slide, isActive) {
      const link = slide.querySelector('[data-announcement-link]');

      if (isActive) {
        slide.removeAttribute('aria-hidden');
        link?.removeAttribute('tabindex');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        link?.setAttribute('tabindex', '-1');
      }
    }

    cancelAnimations() {
      this.activeAnimations.forEach((animation) => {
        try {
          animation.cancel();
        } catch (error) {
          // Ignore already-finished animations.
        }
      });
      this.activeAnimations = [];
      this.isTransitioning = false;
    }

    resetSlide(slide, isActive) {
      slide.getAnimations?.().forEach((animation) => animation.cancel());
      slide.classList.toggle('is-active', isActive);
      slide.style.transform = isActive ? 'translate3d(0, 0, 0)' : 'translate3d(0, 100%, 0)';
      slide.style.visibility = isActive ? 'visible' : 'hidden';
      slide.style.zIndex = isActive ? '1' : '0';
      slide.style.pointerEvents = isActive ? 'auto' : 'none';
      this.setSlideAccessibility(slide, isActive);
    }

    animateSlide(slide, fromY, toY, duration) {
      const animation = slide.animate(
        [
          { transform: `translate3d(0, ${fromY}, 0)` },
          { transform: `translate3d(0, ${toY}, 0)` },
        ],
        {
          duration,
          easing: EASING,
          fill: 'forwards',
        }
      );

      this.activeAnimations.push(animation);
      return animation;
    }

    async show(index, { direction = 1, immediate = false } = {}) {
      if (this.slides.length === 0 || this.isTransitioning) return;

      const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      if (nextIndex === this.currentIndex) return;

      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[nextIndex];
      const goingForward = direction >= 0;
      const enterFrom = goingForward ? '100%' : '-100%';
      const leaveTo = goingForward ? '-100%' : '100%';
      const duration = immediate ? 0 : this.transitionDuration;

      this.cancelAnimations();
      this.isTransitioning = true;

      currentSlide.classList.remove('is-active');
      currentSlide.style.visibility = 'visible';
      currentSlide.style.pointerEvents = 'none';
      currentSlide.style.zIndex = '2';
      this.setSlideAccessibility(currentSlide, false);

      nextSlide.classList.add('is-active');
      nextSlide.style.visibility = 'visible';
      nextSlide.style.pointerEvents = 'auto';
      nextSlide.style.zIndex = '1';
      nextSlide.style.transform = `translate3d(0, ${enterFrom}, 0)`;
      this.setSlideAccessibility(nextSlide, true);

      this.currentIndex = nextIndex;

      if (duration <= 0 || typeof nextSlide.animate !== 'function') {
        this.resetSlide(currentSlide, false);
        this.resetSlide(nextSlide, true);
        this.isTransitioning = false;
        return;
      }

      try {
        const leaveAnimation = this.animateSlide(currentSlide, '0', leaveTo, duration);
        const enterAnimation = this.animateSlide(nextSlide, enterFrom, '0', duration);
        await Promise.all([leaveAnimation.finished, enterAnimation.finished]);
      } catch (error) {
        // Animation was cancelled by a newer transition.
        return;
      }

      this.resetSlide(currentSlide, false);
      this.resetSlide(nextSlide, true);
      this.activeAnimations = [];
      this.isTransitioning = false;
    }

    start() {
      this.stop();
      if (this.slides.length < 2 || this.pauseReasons.size > 0 || document.hidden) {
        return;
      }

      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.show(this.currentIndex + 1, { direction: 1 });
        this.start();
      }, this.interval);
    }

    stop() {
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.stop();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.start();
    }
  }

  customElements.define(componentName, VerticalAnnouncement);
})();
